import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { NativeModules, StyleSheet, View } from 'react-native';
import { Dirs, FileSystem } from 'react-native-file-access';
import { TouchableOpacity } from 'react-native-gesture-handler';
import InAppReview from 'react-native-in-app-review';
import { Easing, useSharedValue } from 'react-native-reanimated';

import Clipboard from '@react-native-clipboard/clipboard';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { Button } from '@/components/ButtonComponent';
import { CustomSwitch } from '@/components/CustomSwitch';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { TextInput } from '@/components/TextInput';
import { Tooltip } from '@/components/Tooltip';
import { LeagueNavigator } from '@/components/league-navigator/components/LeagueNavigator';
import { MOCK_LEAGUES } from '@/components/league-navigator/utils/league-navigator-utils';
import { Box, Column, Row, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Modal } from '@/feature/alerts/components/Modal';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import {
    activateRemoteConfig,
    getAllRemoteConfigs,
    getRemoteConfigByKey,
    refetchRemoteConfigs,
    remoteConfig,
} from '@/feature/analytics/hooks/use-firebase-remote-config';
import { MysteryWheel, MysteryWheelRef } from '@/feature/betslip-pickem/components/mystery-wheel/MysteryWheel';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { createPlayerEventKey } from '@/feature/betslip-pickem/utils/markets-cache-store-utils';
import { Flags, useLocalFlagsStore } from '@/feature/devsettings/hooks/use-dev-settings';
import { useLinkingStore } from '@/feature/entry-share/hooks/use-linking';
import { Share } from '@/feature/entry-share/utils/share';
import { kycRouteNames } from '@/feature/kyc/const';
import { DEEP_LINK_REFERRAL } from '@/feature/partner-deeplink/utils/partner-link-import-utils';
import { useDepositMatchPromos } from '@/feature/promohub/hooks/use-deposit-match';
import {
    PUSH_PRIMER_IGNORE_PERIOD,
    PUSH_PRIMER_SKIP_COUNT,
} from '@/feature/push-primer/hooks/use-push-primer-eligibility';
import { useCalendarSheet } from '@/feature/transactions/components/CalendarSheetProvider';
import { useWhatsNewStore } from '@/feature/whats-new/hooks/use-whats-new-store';
import { ONE_TIME_SHOW_KEYS } from '@/hooks/one-time-show-keys';
import { useActiveWalletStore } from '@/hooks/use-active-wallet';
import { useAuthUserConfig, userIdSelector } from '@/hooks/use-auth-user-config';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { OneTimeShowKeys, useOneTimeShowStore, useShowOneTime } from '@/hooks/use-show-one-time';
import { useUserStore } from '@/hooks/use-user-store';
import { navigateToKyc } from '@/navigation/navigation';
import { RootStackParamList } from '@/navigation/types';
import { useBonusEngineClient, useGraphqlClient, useTradingCardsClient } from '@/providers';
import { common } from '@/styles/styles';
import { remove } from '@/utils/async-storage';
import { isWeb } from '@/utils/constants-platform-specific';
import { FloatingModalProvider, useFloatingModal } from '@/utils/floatingModal/FloatingModalProvider';
import { logger, logs } from '@/utils/logging';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { FlashList } from '@shopify/flash-list';
import { encode as base64Encode } from 'base-64';

import { useKycFieldsStore } from '../../kyc/hooks/use-kyc-fields';
import { CasinoGameLauncher } from '../components/CasinoGameLauncher';
import { HapticDemo } from '../components/HapticDemo';
import { InvocationsTracker } from '../components/InvocationsTracker';
import { PaymentMethodDebugFlags } from '../components/PaymentMethodDebugFlags';
import { WithdrawVerificationDebug } from '../components/WithdrawVerificationDebug';

const style = StyleSheet.create({
    root: {
        flex: 1,
        backgroundColor: ds.colorValue.background.neutral.base,
    },
    settingRow: {
        flexDirection: 'row',
        marginVertical: 8,
        alignItems: 'center',
    },
    row: {
        padding: 4,
        flexDirection: 'column',
    },
    label: {
        marginEnd: 4,
        textTransform: 'uppercase',
    },
    debug: {
        color: '#000',
        backgroundColor: '#DDD',
    },
    info: {
        color: '#000',
        backgroundColor: '#BBB',
    },
    warn: {
        color: '#000',
        backgroundColor: '#C60',
    },
    error: {
        color: '#000',
        backgroundColor: '#D44',
    },
    expandBtn: {
        alignSelf: 'flex-end',
    },
    textFromExpandable: { flex: 1 },
    modalText: { color: 'green', textAlign: 'center' },
});

const TextCollapsable = ({ children }: PropsWithChildren) => {
    const [noOfLines, setNoOfLines] = useState<number | undefined>(10);

    return (
        <View>
            <TouchableOpacity
                onPress={() => {
                    Clipboard.setString(children as string);
                }}
            >
                <Text numberOfLines={noOfLines}>{children}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setNoOfLines(undefined)}>
                <Text style={style.expandBtn} fontWeight="bold">
                    EXPAND
                </Text>
            </TouchableOpacity>
            <SizedBox value={20} />
        </View>
    );
};

const easingEnum = {
    EASE: Easing.ease,
    LINEAR: Easing.linear,
    BOUNCE: Easing.bounce,
    CIRCLE: Easing.circle,
    QUAD: Easing.quad,
    CUBIC: Easing.cubic,
    EXP: Easing.exp,
    SIN: Easing.sin,
} as const;

const mysteryMultipliers = [3.5, 2, 1000, 2, 3.5, 100, 2, 3.5, 25, 2, 3.5, 10, 2, 3.5, 5, 2];

export const DeveloperScreen = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const { navigate, reset } = navigation;
    const {
        localFlags,
        actions: { resetFlags },
    } = useLocalFlagsStore();
    const resetKycValues = useKycFieldsStore(state => state.reset); // TODO: Remove this line when KYC flow is taken out of developer mode
    const { showInfoSheet, showSelectionSheet, showToast, clearAllToasts } = useAlerts();
    const animationsModalRef = useRef<BottomSheetModal>(null);
    const oneLinkData = useLinkingStore(state => state.oneLinkData);
    const clearOneLinkData = useLinkingStore(state => state.setOneLinkData);
    const [easing, setEasing] = useState<keyof typeof easingEnum>('EASE');
    const product = useJurisdictionStore(state => state.product);
    const BONUS_ENGINE_V2_ENABLED = useJurisdictionStore(
        state => state.jurisdictionSettings?.globalSettings?.featureFlags?.bonus_engine_v2_enabled?.enabled
    );
    const setProduct = useJurisdictionStore(state => state.actions.setProduct);
    const { apiUrl, setApiUrl } = useBonusEngineClient();
    const { apiUrl: tradingCardsApiUrl, setApiUrl: setTradingCardsApiUrl } = useTradingCardsClient();
    const graphqlClient = useGraphqlClient();
    const { showCalendarSheetModal } = useCalendarSheet();
    const { data: depositMatchPromos } = useDepositMatchPromos(product, { enabled: BONUS_ENGINE_V2_ENABLED });

    const { data: globalUserId } = useAuthUserConfig({ select: userIdSelector });

    const [firebaseConfigKeys, setFirebaseConfigKeys] = useState(Object.keys(getAllRemoteConfigs() ?? {}));

    const [fantasyApiUrl, setFantasyApiUrl] = useState('http://localhost:8080/graphql');
    const [bonusEngineApiUrl, setBonusEngineApiUrl] = useState(apiUrl);
    const [ripsApiUrl, setRipsApiUrl] = useState(tradingCardsApiUrl);
    const [testLeagueId, setTestLeagueId] = useState('');
    const [selectedLeagueId, setSelectedLeagueId] = useState(MOCK_LEAGUES[0].id);

    const wheelRef = useRef<MysteryWheelRef>(null);
    const rotation = useSharedValue(0);

    const betslip = useBetslipStore(state => state.betslip);
    const betslipDeeplink = useMemo(() => {
        if (betslip.length <= 1) {
            return null;
        }
        const picks = betslip.map(
            pick => `${pick.player.id}:${pick.eventId}:${pick.projection.key}:${pick.outcome}:${pick.projection.type}`
        );
        const encodedPicks = base64Encode(picks.join(','));
        return `${DEEP_LINK_REFERRAL}?picks=${encodedPicks}&partner=dev`;
    }, [betslip]);

    //Add a "tick" so logs that are a static property, will re-render every 2 seconds
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => {
            setTick(new Date().getTime());
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    const oneLinkRows: [string, string | undefined][] = [
        ['VALUE (deep_link_value)', oneLinkData?.data.deep_link_value],
        ['DEFERRED (isDeferred)', oneLinkData ? String(oneLinkData.isDeferred) : undefined],
        ['PROMO CODE (deep_link_sub1)', oneLinkData?.data.deep_link_sub1],
        ['VERTICAL (deep_link_sub2)', oneLinkData?.data.deep_link_sub2],
        [
            'RAW PAYLOAD (all params incl. deep_link_sub3-10)',
            oneLinkData ? JSON.stringify(oneLinkData.data, null, 2) : undefined,
        ],
    ];

    return (
        <Screen>
            <FloatingModalProvider>
                <ScreenNavBar title="Developer screen" />
                <FlashList
                    data={logs}
                    extraData={tick}
                    estimatedItemSize={20}
                    ListHeaderComponentStyle={common.zIndex1}
                    ListHeaderComponent={
                        <Column gap={'s8'}>
                            <NewArchEnabled />
                            <ExpandableComponent title="Navigation Presentations">
                                <Button label="Open Demo" onPress={() => navigate('DevDemoStack')} />
                                <SizedBox value={8} />
                                <Button
                                    label="Open Screen Presets Demo"
                                    onPress={() => navigate('DevDemoStack', { screen: 'ScreenPresetsDemo' })}
                                />
                            </ExpandableComponent>
                            <ExpandableComponent title="Onboarding Flow">
                                <Box>
                                    <Button
                                        label="Simulate KYC Success"
                                        onPress={() =>
                                            navigateToKyc(navigation, { screen: kycRouteNames.VERIFY_IDENTITY })
                                        }
                                    />
                                </Box>
                            </ExpandableComponent>
                            <ExpandableComponent title="Haptic Feedback Test">
                                <HapticDemo />
                            </ExpandableComponent>
                            <ExpandableComponent title="League Navigator">
                                <LeagueNavigator
                                    onViewAllPress={() => {}}
                                    onSelectLeague={league => setSelectedLeagueId(league.id)}
                                    selectedId={selectedLeagueId}
                                    leagues={MOCK_LEAGUES}
                                />
                            </ExpandableComponent>
                            <ExpandableComponent title="OneLink / Deep link">
                                <Box gap={'s8'}>
                                    {oneLinkRows.map(([label, value]) => (
                                        <Text key={label}>{`${label}: ${value || 'None'}`}</Text>
                                    ))}
                                    <Button
                                        label="Clear OneLink data"
                                        onPress={() => {
                                            clearOneLinkData(undefined);
                                        }}
                                    />
                                </Box>
                            </ExpandableComponent>
                            <ExpandableComponent title="API Settings">
                                <Column
                                    p={'s12'}
                                    m={'s8'}
                                    borderRadius={'r12'}
                                    borderWidth={1}
                                    borderColor={ds.colorToken.border.strong}
                                >
                                    <Text>Connect the Fantasy app to a local running server</Text>
                                    <TextInput
                                        value={fantasyApiUrl}
                                        label={'Graphql API'}
                                        onChangeText={e => {
                                            setFantasyApiUrl(e);
                                        }}
                                    />
                                    <SizedBox value={8} />
                                    <Button
                                        label={'Connect'}
                                        onPress={() => {
                                            graphqlClient.recreateGraphqlClient({ url: fantasyApiUrl });
                                        }}
                                    />
                                </Column>
                                <Column
                                    p={'s12'}
                                    m={'s8'}
                                    borderRadius={'r12'}
                                    borderWidth={1}
                                    borderColor={ds.colorToken.border.strong}
                                >
                                    <Text>Connect the Fantasy app to a local running Bonuse Engine server</Text>
                                    <TextInput
                                        value={bonusEngineApiUrl}
                                        label={'Bonus Engine API'}
                                        onChangeText={e => {
                                            setBonusEngineApiUrl(e);
                                        }}
                                    />
                                    <SizedBox value={8} />
                                    <Button
                                        label={'Connect'}
                                        onPress={() => {
                                            setApiUrl(bonusEngineApiUrl);
                                        }}
                                    />
                                </Column>
                                <Column
                                    p={'s12'}
                                    m={'s8'}
                                    borderRadius={'r12'}
                                    borderWidth={1}
                                    borderColor={ds.colorToken.border.strong}
                                >
                                    <Text>Connect the Rips product to a local running Trading Cards server</Text>
                                    <TextInput
                                        value={ripsApiUrl}
                                        label={'Trading Cards API'}
                                        onChangeText={e => {
                                            setRipsApiUrl(e);
                                        }}
                                    />
                                    <SizedBox value={8} />
                                    <Button
                                        label={'Connect'}
                                        onPress={() => {
                                            setTradingCardsApiUrl(ripsApiUrl);
                                        }}
                                    />
                                </Column>
                            </ExpandableComponent>
                            <ExpandableComponent title="Dev Settings">
                                {Object.keys(localFlags).map((id: string) => (
                                    <SettingSwitch key={id} id={id as Flags} />
                                ))}
                                <Button label={'reset to default flags'} onPress={resetFlags} />
                                <SizedBox value={8} />
                                <PaymentMethodDebugFlags />
                            </ExpandableComponent>
                            <ExpandableComponent title="Sweeps Navigation">
                                <Column
                                    p={'s12'}
                                    m={'s8'}
                                    borderRadius={'r12'}
                                    borderWidth={1}
                                    borderColor={ds.colorToken.border.strong}
                                >
                                    <Text>Navigate to a league by ID for testing</Text>
                                    <TextInput
                                        value={testLeagueId}
                                        label={'League ID'}
                                        onChangeText={setTestLeagueId}
                                    />
                                    <SizedBox value={8} />
                                    <Button
                                        label={'Navigate to League (Sweeps)'}
                                        onPress={() => {
                                            if (testLeagueId) {
                                                setProduct(Product.Sweeps);
                                                useActiveWalletStore.getState().resetActiveWallet();
                                                reset({
                                                    index: 0,
                                                    routes: [
                                                        {
                                                            name: 'SweepsHome',
                                                            params: {
                                                                screen: 'SweepsLobby',
                                                                params: {
                                                                    screen: 'SweepsLobbyScreen',
                                                                    params: { leagueId: testLeagueId },
                                                                },
                                                            },
                                                        },
                                                    ],
                                                });
                                            }
                                        }}
                                    />
                                </Column>
                            </ExpandableComponent>
                            <ExpandableComponent title="Casino Game Launcher">
                                <CasinoGameLauncher />
                            </ExpandableComponent>
                            <ExpandableComponent title="Access Tokens">
                                <TouchableOpacity
                                    onPress={() => {
                                        Clipboard.setString(useUserStore.getState().profile.sub);
                                        showToast({ message: 'User ID copied to clipboard.' });
                                    }}
                                >
                                    <Text padding={'s16'}>{`UserId: ${useUserStore.getState().profile.sub}`}</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    onPress={() => {
                                        Clipboard.setString(globalUserId ?? '');
                                        showToast({ message: 'Global User ID copied to clipboard.' });
                                    }}
                                >
                                    <Text padding={'s16'} paddingTop={'s0'}>{`Global UserId: ${
                                        globalUserId ?? ''
                                    }`}</Text>
                                </TouchableOpacity>
                                <Button
                                    label={'Invalidate access token'}
                                    onPress={() => {
                                        const session = { ...useUserStore.getState().session };
                                        if (session?.access_token) {
                                            session.access_token = 'invalid';
                                        }
                                        useUserStore.getState().actions.setSession(session);
                                    }}
                                />
                                <SizedBox value={8} />
                                <Button
                                    label={'Invalidate refresh token'}
                                    onPress={() => {
                                        const session = { ...useUserStore.getState().session };
                                        if (session?.refresh_token) {
                                            session.refresh_token = 'invalid';
                                        }
                                        useUserStore.getState().actions.setSession(session);
                                    }}
                                />
                                <SizedBox value={8} />
                                <Button
                                    label="Start KYC Flow"
                                    onPress={() => {
                                        resetKycValues();
                                        navigateToKyc(navigation, { screen: 'KycSsn' }); // Navigate to first step of KYC Flow (In the meantime is Ssn)
                                    }}
                                />
                                <SizedBox value={8} />
                                <Button
                                    label="Go to KYC Selector"
                                    onPress={() => navigateToKyc(navigation, { screen: 'KycSelector' })}
                                />
                            </ExpandableComponent>
                            <ExpandableComponent title="Withdraw Verification Gate">
                                <WithdrawVerificationDebug />
                            </ExpandableComponent>
                            <ExpandableComponent title="Firebase Remote Configs">
                                <Column>
                                    <Text p="s8">{`Last fetch status: ${remoteConfig.lastFetchStatus}`}</Text>
                                    {firebaseConfigKeys.map(key => {
                                        const config = getRemoteConfigByKey(key);
                                        return (
                                            <Row key={key} justifyContent={'space-between'} p={'s8'}>
                                                <Text color={ds.colorToken.text.brand}>{key}</Text>
                                                <Text variant={'bodyLarge'}>
                                                    {`${config.asString()} (${config.getSource()})`}
                                                </Text>
                                            </Row>
                                        );
                                    })}
                                    <Button
                                        label={'Refetch Firebase Configs'}
                                        onPress={() => {
                                            refetchRemoteConfigs()
                                                .then(() => activateRemoteConfig())
                                                .then(() => {
                                                    showToast({ message: 'Firebase settings refetched' });
                                                    setFirebaseConfigKeys(Object.keys(getAllRemoteConfigs()));
                                                })
                                                .catch(() => {
                                                    showToast({
                                                        message: "Couldn't refetch. Too many retries ?",
                                                        toastType: 'warning',
                                                    });
                                                });
                                        }}
                                    />
                                </Column>
                            </ExpandableComponent>
                            <ExpandableComponent title="One Time Show Debug">
                                <OneTimeShowDebugSection />
                            </ExpandableComponent>
                            <ExpandableComponent title="Invocations Tracker">
                                <InvocationsTracker />
                            </ExpandableComponent>
                            <ExpandableComponent title="What's New">
                                <WhatsNewDebugSection />
                            </ExpandableComponent>
                            <ExpandableComponent title="Push Primer Options">
                                <Button
                                    label="Reset PushPrimer local storage"
                                    onPress={async () => {
                                        await remove(PUSH_PRIMER_SKIP_COUNT);
                                        await remove(PUSH_PRIMER_IGNORE_PERIOD);
                                    }}
                                />
                                <Button label="Go to PushPrimer screen" onPress={() => navigate('PushPrimer')} />
                            </ExpandableComponent>
                            <ExpandableComponent title="Test modals">
                                <Button
                                    label={'Info Sheet'}
                                    onPress={() => {
                                        showInfoSheet({
                                            title: 'Title',
                                            description: 'Description\n Description.',
                                            buttonLabel: 'OK',
                                            handlePress: () => {
                                                // nothing here but,
                                                // dismiss will still be called on button press
                                            },
                                        });
                                    }}
                                />
                                <Button
                                    label={'Toast notification'}
                                    onPress={() => {
                                        showToast({ message: 'Player added' });
                                    }}
                                />
                                <Button
                                    label={'Tappable toast notification'}
                                    onPress={() => {
                                        showToast(
                                            {
                                                message: 'Tap me to open a sheet',
                                                toastType: 'warning',
                                                onPress: () => {
                                                    clearAllToasts();
                                                    showInfoSheet({
                                                        title: 'Toast tapped',
                                                        description: 'The toast press reached its handler.',
                                                        buttonLabel: 'OK',
                                                    });
                                                },
                                            },
                                            5000
                                        );
                                    }}
                                />
                                <Button
                                    label={'Info Sheet w/ secondary button'}
                                    onPress={() => {
                                        showInfoSheet({
                                            title: 'Title',
                                            description: 'Description\n Description.',
                                            buttonLabel: 'OK',
                                            secondaryLabel: 'Secondary Btn',
                                            handlePress: () => {
                                                showInfoSheet({
                                                    title: 'Inner Title',
                                                    description: 'Inner description',
                                                    buttonLabel: 'OK',
                                                    onDismiss: () => {
                                                        showToast({ message: 'Dismissed inner modal!' });
                                                    },
                                                });
                                            },
                                            onDismiss: () => {
                                                showToast({ message: 'Dismissed outer modal!' });
                                            },
                                        });
                                    }}
                                />
                                <Button
                                    label={'Info Error Sheet'}
                                    onPress={() => {
                                        showInfoSheet({
                                            title: 'Title',
                                            description: 'Description\n Description.',
                                            buttonLabel: 'OK',
                                            showCancel: true,
                                            errorStyling: true,
                                        });
                                    }}
                                />
                                <Button
                                    label={'Selection Sheet'}
                                    onPress={() => {
                                        const items = [
                                            { id: '1', label: 'Item 1', extra: 'extra' },
                                            { id: '2', label: 'Item 2' },
                                            { id: '3', label: 'Item 3' },
                                        ];
                                        showSelectionSheet({
                                            items,
                                            title: 'Selection title',
                                            selection: items[0],
                                            onItemSelected: i => {
                                                console.log('selected', i);
                                            },
                                        });
                                    }}
                                />
                                <Button
                                    label={'Calendar Sheet'}
                                    onPress={() => {
                                        showCalendarSheetModal({
                                            closeIconMode: 'close',
                                            showMonthYearHeader: isWeb,
                                        });
                                    }}
                                />
                                <NonDismissibleModalSection />
                                <ExpandableComponent title={" Test Modals' animations"}>
                                    {Object.keys(easingEnum).map(key => {
                                        return (
                                            <Box mb={'s10'}>
                                                <Button
                                                    key={key}
                                                    label={key}
                                                    onPress={() => {
                                                        setEasing(key as keyof typeof easingEnum);
                                                        animationsModalRef.current?.present();
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })}
                                    <Modal
                                        id="animations-modal"
                                        modalRef={animationsModalRef}
                                        animation={{
                                            easing: easingEnum[easing],
                                            duration: 150,
                                        }}
                                    >
                                        <Box px={'s48'} py={'s96'}>
                                            <Text variant="buttonLabel"> {easing} </Text>
                                        </Box>
                                    </Modal>
                                </ExpandableComponent>
                            </ExpandableComponent>
                            <ExpandableComponent title="Crash app">
                                <Box gap={'s8'}>
                                    <Button
                                        label="Crash native main thread"
                                        onPress={() =>
                                            NativeModules.PerfKiller.crashNativeMainThread(
                                                'dev - crash native main thread'
                                            )
                                        }
                                    />
                                    <Button
                                        label="Crash javascript thread"
                                        onPress={() => {
                                            throw new Error('dev - crash javascript thread');
                                        }}
                                    />
                                </Box>
                            </ExpandableComponent>
                            <ExpandableComponent title="Deposit match promos">
                                <Box>
                                    {depositMatchPromos?.map(promo => (
                                        <Column key={promo.slug} ml={'s8'} mb={'s16'}>
                                            <Text>Promo: {promo.slug}</Text>
                                            <Text>ExpirationTime: {promo.expirationTime}</Text>
                                            <Text>MaxAmount: {promo.maxAmount}</Text>
                                            <Text>Percentage: {promo.percentage}</Text>
                                        </Column>
                                    ))}
                                </Box>
                            </ExpandableComponent>
                            <ExpandableComponent title="Mystery Wheel">
                                <Box mb={'s20'}>
                                    <Button label="start rotation" onPress={() => wheelRef.current?.startRotation()} />
                                    <Button
                                        label="stop rotation"
                                        onPress={() => {
                                            const randomMultiplierIndex = Math.floor(Math.random() * 16);
                                            wheelRef.current?.stopRotation(mysteryMultipliers[randomMultiplierIndex]);
                                        }}
                                    />
                                </Box>
                                <MysteryWheel ref={wheelRef} multipliers={mysteryMultipliers} rotation={rotation} />
                            </ExpandableComponent>
                            <ExpandableComponent title="In App Rating">
                                <Button
                                    label="Request In App Review"
                                    onPress={() => {
                                        if (isWeb) {
                                            return;
                                        }
                                        InAppReview.RequestInAppReview()
                                            .then(hasFlowFinishedSuccessfully => {
                                                logger.info('request review success', hasFlowFinishedSuccessfully);
                                            })
                                            .catch(e => logger.warn('error requesting review', e));
                                    }}
                                />
                            </ExpandableComponent>
                            <ExpandableComponent title="Betslip Picks Deeplink">
                                <TouchableBox
                                    mx={'s8'}
                                    disabled={!betslipDeeplink}
                                    onPress={() => {
                                        if (!betslipDeeplink) {
                                            return;
                                        }
                                        Clipboard.setString(betslipDeeplink);
                                        showToast({ message: 'Deeplink copied to clipboard.' });
                                    }}
                                >
                                    <Text>{betslipDeeplink ?? 'Add at least 2 picks for deeplink'}</Text>
                                </TouchableBox>
                            </ExpandableComponent>
                            <ExpandableComponent title="Player Profile Deeplink">
                                {betslip.map(pick => {
                                    return (
                                        <TouchableBox
                                            mx={'s8'}
                                            key={createPlayerEventKey(pick.player.id, pick.eventId)}
                                            disabled={betslip.length === 0}
                                            onPress={() => {
                                                const deeplink = `betrpickem://picks/home/${pick.player.league}/${pick.eventId}/${pick.player.id}`;
                                                Clipboard.setString(deeplink);
                                                showToast({ message: 'Player deeplink copied to clipboard.' });
                                            }}
                                        >
                                            <Text>
                                                {`Copy deeplink for ${pick.player.lastName} ${pick.player.firstName}`}
                                            </Text>
                                        </TouchableBox>
                                    );
                                })}
                            </ExpandableComponent>
                            <Row style={[common.flex, common.justifySpaceBetween]} padding={'s14'}>
                                <Text variant={'headlineSmall'}>Test Tooltip</Text>
                                <Tooltip
                                    tooltipText="It's a tooltip. It's a tooltip. It's a tooltip.It's a tooltip. It's a tooltip.It's a tooltip."
                                    direction="bottom"
                                >
                                    <Box>
                                        <Text>Click Me</Text>
                                    </Box>
                                </Tooltip>
                            </Row>
                            <ShareLogsButton />
                            <ShowFloatingModal />
                            <OneTimeTooltipButton />
                        </Column>
                    }
                    renderItem={({ item }) => {
                        return (
                            <View style={style.row}>
                                <View style={[common.row, common.spaceBetweenRow, style.label, style[item.type]]}>
                                    <Text style={[style.label, style[item.type]]}>{item.msg}</Text>
                                    <Text style={style[item.type]}>
                                        [{new Date(item.timestamp).toLocaleTimeString()}]
                                    </Text>
                                </View>
                                <TextCollapsable>{item.details}</TextCollapsable>
                            </View>
                        );
                    }}
                />
            </FloatingModalProvider>
        </Screen>
    );
};

const ShowFloatingModal = () => {
    const { openModal } = useFloatingModal();

    return (
        <Box zIndex={-2} gap={'s10'}>
            <Button
                label={'Open Floating Modal NFL'}
                onPress={() => {
                    openModal({
                        url: 'https://gsm-widgets-uat.betstream.betgenius.com/multisportgametracker?productName=gt-sidebyside&fixtureId=11061059',
                        minimizedContentScale: 0.6,
                        maximizedContentScale: 0.9,
                        minimizedContentHeight: 180,
                        maximizedContentHeight: 280,
                    });
                }}
            />
            <Button
                label={'Open Floating Modal NBA'}
                onPress={() => {
                    openModal({
                        url: 'https://gsm-widgets-uat.betstream.betgenius.com/multisportgametracker?productName=gt-sidebyside&fixtureId=11351604',
                        minimizedContentScale: 0.6,
                        maximizedContentScale: 0.9,
                        minimizedContentHeight: 180,
                        maximizedContentHeight: 280,
                    });
                }}
            />
        </Box>
    );
};

const OneTimeTooltipButton = () => {
    const { show: showOneTimeTooltip, handleInteraction } = useShowOneTime('SBK_DEVScreen_GameTrackerTooltip');

    useEffect(() => {
        return () => {
            handleInteraction();
        };
    }, [handleInteraction]);

    return (
        <Box zIndex={-2}>
            {showOneTimeTooltip ? (
                <>
                    <Tooltip
                        tooltipText="This is a one time show tooltip. After you close it or go away from this screen it won't be seen again."
                        tooltipContentPosition={{ right: 30, top: 32 }}
                        initialState="visible"
                        performSideEffectOnClose={handleInteraction}
                    >
                        <Button label={'One Time Show Button'} onPress={handleInteraction} />
                    </Tooltip>
                </>
            ) : null}
        </Box>
    );
};

const ExpandableComponent = (props: PropsWithChildren & { title: string }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <Column padding={'s4'}>
            <TouchableOpacity
                onPress={() => {
                    setExpanded(!expanded);
                }}
            >
                <Row alignItems={'center'}>
                    <SizedBox value={8} />
                    <Text variant={'headlineSmall'} style={style.textFromExpandable}>
                        {props.title}
                    </Text>
                </Row>
            </TouchableOpacity>
            {expanded ? props.children : null}
        </Column>
    );
};

const SettingSwitch: React.FC<{
    id: Flags;
}> = ({ id }) => {
    const {
        actions: { changeFlag },
        localFlags,
    } = useLocalFlagsStore();
    const { showInfoSheet } = useAlerts();
    return (
        <View style={style.settingRow}>
            <CustomSwitch
                val={localFlags[id]?.enabled || false}
                handleOnPress={() => {
                    changeFlag(id);
                }}
            />
            <SizedBox value={8} />
            <TouchableOpacity
                onPress={() => {
                    showInfoSheet({
                        title: 'Description',
                        description: localFlags[id]?.description,
                        buttonLabel: 'OK',
                    });
                }}
            >
                <Text>{localFlags[id]?.label}</Text>
            </TouchableOpacity>
        </View>
    );
};

const NonDismissibleModalSection = () => {
    const sheetRef = useRef<BottomSheetModal>(null);
    const data = useMemo(
        () =>
            Array(10)
                .fill(0)
                .map((_, index) => index),
        []
    );

    return (
        <>
            <Button
                label={'Not dismissible by drag/overlay press'}
                onPress={() => {
                    sheetRef.current?.present();
                }}
            />
            <Modal modalRef={sheetRef} id={'test modal'} dismissible={false}>
                {data.map(item => (
                    <View key={item}>
                        <Text style={style.modalText}>{1 + item}</Text>
                    </View>
                ))}
                <Button label="Close" onPress={() => sheetRef.current?.dismiss()} />
            </Modal>
        </>
    );
};

const NewArchEnabled = () => {
    // Fabric - new arch
    // Paper - old arch
    const uiManager = global?.nativeFabricUIManager ? 'Fabric' : 'Paper';

    return (
        <Row paddingHorizontal={'s14'} justifyContent={'space-between'}>
            <Text variant={'headlineSmall'}>UI Manager</Text>
            <Text variant={'headlineSmall'}>{uiManager}</Text>
        </Row>
    );
};

async function shareLogsFile() {
    try {
        const dump = JSON.stringify(
            {
                metadata: { createdAt: new Date().toISOString() },
                logs,
            },
            null,
            2
        );

        const filename = `logs-${Date.now()}.json`;
        const path = `${Dirs.CacheDir}/${filename}`;

        await FileSystem.writeFile(path, dump, 'utf8');

        await Share.open({
            url: `file://${path}`,
            type: 'application/json',
            filename, //
            failOnCancel: false,
        });
    } catch (err: any) {
        console.warn('shareLogsFile error', err?.message ?? err);
    }
}

const OneTimeShowKeyRow = ({ storageKey }: { storageKey: OneTimeShowKeys }) => {
    const { show, handleInteraction } = useShowOneTime(storageKey);
    const setShow = useOneTimeShowStore(s => s.setShow);
    const { showToast } = useAlerts();

    const toggle = async () => {
        if (!show) {
            await remove(storageKey);
            setShow(storageKey, true);
            showToast({ message: `Reset: ${storageKey}` });
        } else {
            handleInteraction();
            showToast({ message: `Shown: ${storageKey}` });
        }
    };

    return (
        <Row justifyContent="space-between" alignItems="center" gap={'s8'} mb={'s8'}>
            <Text variant="bodySmall">{storageKey}</Text>
            <Text color={show ? ds.colorToken.text.brand : 'neutral500'}>{show ? 'showing' : 'hidden'}</Text>
            <Button label="Toggle" onPress={toggle} />
        </Row>
    );
};

const OneTimeShowDebugSection = () => {
    const setShow = useOneTimeShowStore(s => s.setShow);
    const { showToast } = useAlerts();

    const handleResetAll = async () => {
        await Promise.all(
            ONE_TIME_SHOW_KEYS.map(async k => {
                await remove(k);
                setShow(k, true);
            })
        );
        showToast({ message: 'All one-time shows reset' });
    };

    return (
        <Box gap={'s8'}>
            {ONE_TIME_SHOW_KEYS.map(key => (
                <OneTimeShowKeyRow key={key} storageKey={key} />
            ))}
            <Button label="Reset all" onPress={handleResetAll} />
        </Box>
    );
};

const ShareLogsButton = () => {
    return <Button label="Share Device Logs" onPress={shareLogsFile} />;
};

const WhatsNewDebugSection = () => {
    const seenCardIds = useWhatsNewStore(state => state.seenCardIds);
    const unmarkCard = useWhatsNewStore(state => state.actions.unmarkCard);
    const resetAll = useWhatsNewStore(state => state.actions.resetAll);
    const { showToast } = useAlerts();

    return (
        <Box gap={'s8'}>
            {seenCardIds.length === 0 ? (
                <Text variant="bodySmall" color={'neutral500'}>
                    No seen cards stored
                </Text>
            ) : (
                seenCardIds.map(cardId => (
                    <Row key={cardId} justifyContent="space-between" alignItems="center">
                        <Box flex={1}>
                            <Text variant="bodySmall">{cardId}</Text>
                        </Box>
                        <Button
                            label="Delete"
                            onPress={() => {
                                unmarkCard(cardId);
                                showToast({ message: `Deleted: ${cardId}` });
                            }}
                        />
                    </Row>
                ))
            )}
            <Button
                label="Reset all"
                onPress={() => {
                    resetAll();
                    showToast({ message: "What's New seen state cleared" });
                }}
            />
        </Box>
    );
};
